<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">Concepts</h5>
          <small class="text-muted">Manage planner concepts by class and subject</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadConcepts"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row concepts-planner-filters mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--cols-3 d-flex align-items-center">
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input"
              :placeholder="$t('Search') + '...'"
              @input="onSearchChange"
            />
            <v-select
              v-model="filters.classID"
              :options="classes"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              placeholder="Class"
              class="filter-control filter-field-item concepts-planner-filter-select planner-filter-select"
              @input="onClassChange"
            />
            <v-select
              v-model="filters.subjectID"
              :options="subjects"
              :clearable="true"
              label="subject"
              :reduce="(val) => val.id"
              placeholder="Subject"
              :disabled="isTeacherScoped && !filters.classID"
              class="filter-control filter-field-item concepts-planner-filter-select planner-filter-select"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Concept</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="concepts"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <div class="list-skeleton">
              <div
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="list-skeleton__row"
                :style="{
                  gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))`,
                }"
              >
                <div
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="list-skeleton__cell"
                >
                  <div
                    class="list-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(concept)="data">
            <b-badge variant="light-primary">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(className)="data">
            <b-badge variant="light-primary">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(subjectName)="data">
            <b-badge variant="light-success">
              {{ data.value }}
            </b-badge>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="Edit"
                class="action-btn-table mr-1"
                @click="editConcept(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                class="action-btn-outline delete-btn"
                @click="deleteConcept(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Concept' : 'Add Concept'"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group label="Class" label-for="class">
              <v-select
                v-model="form.classID"
                :options="classes"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select class"
                @input="onFormClassChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Subject" label-for="subject">
              <v-select
                v-model="form.subjectID"
                :options="formSubjects"
                :clearable="false"
                label="subject"
                :reduce="(val) => val.id"
                placeholder="Select subject"
                :disabled="isTeacherScoped && !form.classID"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group label="Concept">
              <b-form-input
                v-model.trim="form.concept"
                placeholder="Enter concept name"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="saveConcept" :disabled="loading">
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import plannerFilterDropdownScrollMixin from "./plannerFilterDropdownScroll";

export default {
  name: "Concepts",
  mixins: [plannerFilterDropdownScrollMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BBadge,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      concepts: [],
      classes: [],
      subjects: [],
      formSubjects: [],
      allSubjects: [],
      teacherClassesData: [],
      assignedClassIDs: [],
      hasTeacherLink: false,
      teacherScopedListCache: null,
      searchQuery: "",
      searchTimeout: null,
      showModal: false,
      isEdit: false,
      loading: false,
      dataLoading: true,
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        { label: "S.No", key: "index", thClass: "text-center", tdClass: "text-center" },
        { key: "concept", label: "Concept" },
        { key: "className", label: "Class" },
        { key: "subjectName", label: "Subject" },
        { key: "actions", label: "Actions", thClass: "text-center", tdClass: "text-center" },
      ],
      form: {
        id: 0,
        classID: null,
        subjectID: null,
        concept: "",
        isActive: true,
        sequence: 0,
        campusID: 0,
      },
      filters: {
        classID: null,
        subjectID: null,
        tID: null,
      },
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    isTeacherScoped() {
      return this.hasTeacherLink && !!this.filters.tID;
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  async created() {
    // Initialize rights and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    await this.loadStaffIdForCurrentUser();
    await this.loadClasses();
    if (!this.isTeacherScoped) {
      await this.loadAllSubjects();
    }
    await this.loadConcepts();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },
    async loadStaffIdForCurrentUser() {
      try {
        const userLinkID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.localID ||
          this.$store.state.userData.userLinkID ||
          0;
        const cID = this.plannerCampusID;
        const dbName = encodeURIComponent(this.$store.state.userData.db);

        if (!userLinkID || !cID) {
          return;
        }

        const staffUrl = `${this.$store.state.domain}Staff/GetByUserLinkID?db=${dbName}&userLinkID=${userLinkID}&cID=${cID}`;

        const staffResponse = await this.get({
          url: staffUrl,
          token: this.$store.state.userData.token,
        });

        let staffID = null;
        if (staffResponse && staffResponse.id) {
          staffID = staffResponse.id;
        } else if (staffResponse && staffResponse.data && staffResponse.data.id) {
          staffID = staffResponse.data.id;
        } else if (
          staffResponse &&
          Array.isArray(staffResponse) &&
          staffResponse.length > 0 &&
          staffResponse[0].id
        ) {
          staffID = staffResponse[0].id;
        } else if (
          staffResponse &&
          Array.isArray(staffResponse.data) &&
          staffResponse.data.length > 0 &&
          staffResponse.data[0].id
        ) {
          staffID = staffResponse.data[0].id;
        }

        if (staffID) {
          this.hasTeacherLink = true;
          this.filters.tID = staffID;
        } else {
          this.hasTeacherLink = false;
          this.filters.tID = null;
        }
      } catch (error) {
        console.error("Error loading staff ID for concepts:", error);
        this.hasTeacherLink = false;
        this.filters.tID = null;
      }
    },

    async loadClasses() {
      if (this.isTeacherScoped) {
        await this.loadTeacherClasses();
        return;
      }

      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.plannerCampusID +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const classesData = await this.get(obj);
        const list = classesData?.data || classesData || [];
        this.classes = Array.isArray(list)
          ? list.map((el) => el.cls).filter(Boolean)
          : [];
        this.assignedClassIDs = [];
        this.teacherClassesData = [];
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.assignedClassIDs = [];
        this.teacherClassesData = [];
      }
    },

    async loadTeacherClasses() {
      if (!this.filters.tID) {
        this.classes = [];
        this.assignedClassIDs = [];
        this.teacherClassesData = [];
        return;
      }

      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const url = `${this.$store.state.domain}teacherClasses/LoadData?db=${dbName}&tID=${this.filters.tID}&cID=${this.plannerCampusID}`;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });

        let data = [];
        if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        } else if (response && response.status === "success" && response.data) {
          data = Array.isArray(response.data) ? response.data : [];
        }

        this.teacherClassesData = data;
        const classMap = new Map();
        data.forEach((classItem) => {
          const classId =
            classItem.cID ?? classItem.clsID ?? classItem.classID ?? null;
          if (classId == null || classMap.has(classId)) return;
          classMap.set(classId, {
            id: classId,
            name:
              classItem.className ||
              classItem.name ||
              classItem.cls?.name ||
              `Class ${classId}`,
          });
        });
        this.classes = Array.from(classMap.values());
        this.assignedClassIDs = this.classes.map((cls) => cls.id);
      } catch (error) {
        console.error("Error loading teacher classes:", error);
        this.classes = [];
        this.assignedClassIDs = [];
        this.teacherClassesData = [];
      }
    },

    getTeacherClassItem(classID) {
      if (!classID) return null;
      return this.teacherClassesData.find((classItem) => {
        const classId =
          classItem.cID ?? classItem.clsID ?? classItem.classID ?? null;
        return Number(classId) === Number(classID);
      });
    },

    getAssignedSubjectsForClass(classID) {
      if (!this.isTeacherScoped || !classID) return [];

      const classItem = this.getTeacherClassItem(classID);
      const subjects = new Map();
      (classItem?.secSubjectsData || []).forEach((section) => {
        (section.subjects || []).forEach((sub) => {
          if (sub?.id != null && !subjects.has(sub.id)) {
            subjects.set(sub.id, {
              id: sub.id,
              subject: sub.subject || sub.name || `Subject ${sub.id}`,
            });
          }
        });
      });
      return Array.from(subjects.values());
    },

    getAssignedSubjectIDsForClass(classID) {
      return this.getAssignedSubjectsForClass(classID).map((sub) => sub.id);
    },

    filterConceptsForTeacher(items) {
      if (!this.isTeacherScoped || !Array.isArray(items)) {
        return items || [];
      }

      return items.filter((item) => {
        const classId = item.classID ?? item.classId;
        const subjectId = item.subjectID ?? item.subjectId;

        if (
          !this.assignedClassIDs.some(
            (id) => Number(id) === Number(classId)
          )
        ) {
          return false;
        }

        const allowedSubjectIDs = this.getAssignedSubjectIDsForClass(classId);
        return allowedSubjectIDs.some(
          (id) => Number(id) === Number(subjectId)
        );
      });
    },

    async loadAllSubjects() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.plannerCampusID,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        if (response && response.data && Array.isArray(response.data)) {
          this.allSubjects = response.data;
        } else if (Array.isArray(response)) {
          this.allSubjects = response;
        } else {
          this.allSubjects = [];
        }
        this.subjects = this.allSubjects;
      } catch (error) {
        console.error("Error loading subjects:", error);
        this.allSubjects = [];
        this.subjects = [];
      }
    },

    updateFilterSubjects(classID) {
      if (this.isTeacherScoped) {
        this.subjects = classID ? this.getAssignedSubjectsForClass(classID) : [];
      } else {
        this.subjects = this.allSubjects;
      }

      if (
        this.filters.subjectID &&
        !this.subjects.some((sub) => sub.id === this.filters.subjectID)
      ) {
        this.filters.subjectID = null;
      }
    },

    updateFormSubjects(classID) {
      if (this.isTeacherScoped) {
        this.formSubjects = classID ? this.getAssignedSubjectsForClass(classID) : [];
      } else {
        this.formSubjects = this.allSubjects;
      }

      if (
        this.form.subjectID &&
        !this.formSubjects.some((sub) => sub.id === this.form.subjectID)
      ) {
        this.form.subjectID = null;
      }
    },

    buildConceptsRequest(page, pageSize) {
      let url =
        this.$store.state.domain +
        "PlannerConcepts/GetByPagination?db=" +
        this.$store.state.userData.db +
        "&campusID=" +
        this.plannerCampusID +
        "&page=" +
        page +
        "&pageSize=" +
        pageSize;

      if (this.isTeacherScoped) {
        if (this.filters.classID) {
          url += "&classID=" + this.filters.classID;
        } else if (this.assignedClassIDs.length > 0) {
          url += "&classIDs=" + this.assignedClassIDs.join(",");
        }
        url += "&tID=" + this.filters.tID;
      } else {
        url += "&classID=" + (this.filters.classID || "");
      }

      url += "&subjectID=" + (this.filters.subjectID || "");
      if (this.searchQuery && this.searchQuery.trim()) {
        url += "&search=" + encodeURIComponent(this.searchQuery.trim());
      }

      return {
        url,
        token: this.$store.state.userData.token,
        dashboard: true,
      };
    },

    invalidateTeacherScopedCache() {
      this.teacherScopedListCache = null;
    },

    async fetchTeacherScopedPaginatedData(cacheKey, fetchPage) {
      if (!this.isTeacherScoped) {
        return null;
      }

      if (
        this.teacherScopedListCache &&
        this.teacherScopedListCache.key === cacheKey
      ) {
        return this.teacherScopedListCache.items;
      }

      const pageSize = 1000;
      let page = 1;
      let allRaw = [];

      while (page <= 50) {
        const res = await fetchPage(page, pageSize);
        const batch = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        const reportedTotal = Number(res?.total ?? 0);

        allRaw = allRaw.concat(batch);

        if (!batch.length || batch.length < pageSize) {
          break;
        }
        if (reportedTotal && allRaw.length >= reportedTotal) {
          break;
        }
        page += 1;
      }

      const filtered = this.filterConceptsForTeacher(allRaw);
      this.teacherScopedListCache = { key: cacheKey, items: filtered };
      return filtered;
    },

    getTeacherScopedPageSlice(allItems) {
      const totalCount = Array.isArray(allItems) ? allItems.length : 0;
      const start = (this.currentPage - 1) * this.perPage;
      return {
        items: allItems.slice(start, start + this.perPage),
        totalCount,
      };
    },

    async loadConcepts(page) {
      if (page) {
        this.currentPage = page;
      }
      this.dataLoading = true;
      try {
        if (this.isTeacherScoped) {
          const cacheKey = [
            this.filters.classID,
            this.filters.subjectID,
            this.searchQuery,
            this.assignedClassIDs.join(","),
          ].join("|");
          const allItems = await this.fetchTeacherScopedPaginatedData(
            cacheKey,
            (p, ps) => this.get(this.buildConceptsRequest(p, ps))
          );
          const { items, totalCount } = this.getTeacherScopedPageSlice(allItems);
          this.concepts = items;
          this.totalCount = totalCount;
        } else {
          const res = await this.get(
            this.buildConceptsRequest(this.currentPage, this.perPage)
          );
          if (res && Array.isArray(res.data)) {
            this.concepts = res.data;
            this.totalCount = res.total;
          } else {
            this.concepts = [];
            this.totalCount = 0;
          }
        }
      } finally {
        this.dataLoading = false;
      }
    },

    openAddModal() {
      this.isEdit = false;
      this.formSubjects = this.isTeacherScoped ? [] : this.allSubjects;
      this.showModal = true;
    },

    editConcept(item) {
      this.isEdit = true;
      this.form = { ...item };
      this.updateFormSubjects(this.form.classID);
      this.showModal = true;
    },

    async deleteConcept(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerConcepts/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Concept deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const success = await this.delete(obj);
        if (success) {
          this.loadConcepts();
        }
      }
    },

    async saveConcept() {
      if (
        !this.form.classID ||
        !this.form.subjectID ||
        !this.form.concept.trim()
      ) {
        this.$bvToast.toast("Please fill all required fields!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;
      this.form.campusID = this.plannerCampusID;

      const userLinkID =
        this.$store.state.userData.userID ||
        this.$store.state.userData.localID ||
        this.$store.state.userData.userLinkID ||
        0;

      const obj = {
        url:
          this.$store.state.domain +
          "PlannerConcepts/save?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.plannerCampusID +
          "&userID=" +
          userLinkID,
        body: this.form,
        message:
          "Concept " + (this.isEdit ? "updated" : "created") + " successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };

      const success = await this.post(obj);

      if (success) {
        this.showModal = false;
        this.loadConcepts();
      }
      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        classID: null,
        subjectID: null,
        concept: "",
        isActive: true,
        sequence: 0,
        campusID: this.plannerCampusID,
      };
      this.formSubjects = [];
    },

    onClassChange(classID) {
      if (this.isTeacherScoped) {
        this.filters.subjectID = null;
      }
      this.invalidateTeacherScopedCache();
      this.updateFilterSubjects(classID);
      this.onFilterChange();
    },

    onFormClassChange(classID) {
      this.form.subjectID = null;
      this.updateFormSubjects(classID);
    },

    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.invalidateTeacherScopedCache();
        this.currentPage = 1;
        this.loadConcepts();
      }, 500);
    },

    onFilterChange() {
      this.invalidateTeacherScopedCache();
      this.currentPage = 1;
      this.loadConcepts();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadConcepts();
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "./plannerListTable.scss";

</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

@import "./plannerFilterDropdownMenu.scss";
</style>
